import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { AlertTriangle, Trash2, UserX, Eye, EyeOff } from 'lucide-react';

const DeleteAccountDialog = ({ isOpen, onClose, onConfirm, user = null }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isValid, setIsValid] = useState(false);

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    validateForm(e.target.value, confirmText);
  };

  const handleConfirmTextChange = (e) => {
    setConfirmText(e.target.value);
    validateForm(password, e.target.value);
  };

  const validateForm = (pwd, confirm) => {
    setIsValid(pwd.length > 0 && confirm === 'DELETE');
  };

  const handleConfirm = () => {
    if (isValid) {
      onConfirm(password);
      // Reset form
      setPassword('');
      setConfirmText('');
      setIsValid(false);
    }
  };

  const handleClose = () => {
    setPassword('');
    setConfirmText('');
    setIsValid(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center text-red-700">
            <UserX className="h-5 w-5 mr-2" />
            Delete Account
          </DialogTitle>
          <DialogDescription>
            {user 
              ? `This action will permanently delete the account for ${user.full_name} and all associated data.`
              : 'This action will permanently delete your account and all associated data.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Warning:</strong> This action cannot be undone. Your entire account and all data will be permanently deleted.
            </AlertDescription>
          </Alert>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Data to be deleted:</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li className="flex items-center">
                <div className="w-2 h-2 bg-red-400 rounded-full mr-2"></div>
                Your user profile and account
              </li>
              <li className="flex items-center">
                <div className="w-2 h-2 bg-red-400 rounded-full mr-2"></div>
                All children profiles and data
              </li>
              <li className="flex items-center">
                <div className="w-2 h-2 bg-red-400 rounded-full mr-2"></div>
                All activities and milestones
              </li>
              <li className="flex items-center">
                <div className="w-2 h-2 bg-red-400 rounded-full mr-2"></div>
                All conversation history
              </li>
              <li className="flex items-center">
                <div className="w-2 h-2 bg-red-400 rounded-full mr-2"></div>
                All AI memories and context
              </li>
              <li className="flex items-center">
                <div className="w-2 h-2 bg-red-400 rounded-full mr-2"></div>
                All usage records and analytics
              </li>
              <li className="flex items-center">
                <div className="w-2 h-2 bg-red-400 rounded-full mr-2"></div>
                All expert ratings and feedback
              </li>
            </ul>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Confirm your password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Enter your password"
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm">Type "DELETE" to confirm</Label>
              <Input
                id="confirm"
                type="text"
                value={confirmText}
                onChange={handleConfirmTextChange}
                placeholder="Type DELETE to confirm"
                className="uppercase"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isValid}
            variant="destructive"
            className="w-full sm:w-auto"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteAccountDialog;
